/**
 * The eval bar's geometry and ratios (fix D / R4).
 *
 * WHY IT IS NOT A STAGE LEAF LIKE THE OTHERS: it only exists while the engine
 * is on, which can never be true during a live game, so it is free to read the
 * analysis store on every result — the Stage itself reads ONE boolean to decide
 * whether to mount it (`stage/Stage/model.ts`), and that boolean cannot move
 * during a game.
 *
 * `WinningBar`'s de-facto contract (`Screens/ChessAnalysis/index.tsx` L1587-1589):
 * `whiteWinRatio` sizes the TOP segment, and `reverse` is what makes that segment
 * white. So the model hands it the TOP player's chance, which is white's exactly
 * when the board is showing black at the bottom.
 *
 * FIX Z1.2 (2026-10-06, owner report: "in an initial equal position even
 * slightly plus the eval bar is tilting towards black"): in ASSIST mode the
 * bar now reads `analysisStore.assistSettled` — the chip `settleAssist`
 * alone commits — never `lines[0]`, the deepening stream that same action
 * clears on every settle (snapping the bar to 0 between searches, or
 * showing whatever partial depth the head is mid-search at). By
 * construction this is the exact number the move list's own chip shows.
 */
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import {orientationFor} from '@/Store/liveGame/selectors';

import {useAnalysis} from '../../rail/analysis/analysisStore';
import {LAYOUT, useV2Ui} from '../../theme/useV2Ui';
import {selectStageColor} from '../Stage/model';

/**
 * Lichess's logistic: a centipawn score becomes white's win chance.
 * `1 / (1 + 10^(-cp/400))` — 0 cp is dead even, +400 is ~0.91.
 * A mate score is certainty, in whichever direction it points.
 */
export const whiteChance = (score: number, isMate: boolean): number => {
  if (isMate) {
    return score > 0 ? 1 : 0;
  }
  return 1 / (1 + Math.pow(10, -score / 400));
};

export type EvalBarModel = {
  /** the TOP segment's share (WinningBar's own contract) */
  topRatio: number;
  bottomRatio: number;
  /** white is at the TOP of the board */
  reverse: boolean;
  /** the bar is as tall as the board and starts where the board does */
  boardSize: number;
  offset: number;
};

export function useEvalBarModel(): EvalBarModel {
  const {score, isMate, terminal} = useAnalysis(
    useShallow(s => {
      const line = s.mode === 'assist' ? s.assistSettled : s.lines[0];
      return {
        score: line?.score ?? 0,
        isMate: line?.isMate ?? false,
        terminal: s.terminal,
      };
    }),
  );
  const seatColor = useLiveGame(selectStageColor);
  const {flipped, boardSize, viewport} = useV2Ui(
    useShallow(s => ({
      flipped: s.flipped,
      boardSize: s.boardSize,
      viewport: s.viewport,
    })),
  );

  const bottom = orientationFor(seatColor, flipped);
  const reverse = bottom === 'black';
  // fix U: a terminal position pins the bar rather than reading a line that
  // was never searched — checkmate to the winner's full extent (the SAME
  // `whiteChance` a forced-mate SCORE already renders, just fed the winner
  // instead of a line's sign) and any draw dead centre.
  const white = terminal
    ? terminal.kind === 'checkmate'
      ? whiteChance(terminal.winner === 'w' ? 1 : -1, true)
      : 0.5
    : whiteChance(score, isMate);
  const topRatio = reverse ? white : 1 - white;

  return {
    topRatio,
    bottomRatio: 1 - topRatio,
    reverse,
    boardSize,
    offset:
      viewport === 'phone'
        ? LAYOUT.boardFramePaddingPhone
        : LAYOUT.boardFrameVerticalPadding,
  };
}

export default useEvalBarModel;
